import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { uploadBallkids, addSingleBallkid, clearAllCollections, exportGroupData } from '../resetAdminToolsFirebase';
import { saveTournamentConfig, invalidateMembersCache } from '../firebaseService';
import { BallkidContext } from '../context/BallkidContext';
import { useTournamentConfig } from '../context/TournamentConfigContext';
import { useGroup, ALL_FEATURES } from '../context/GroupContext';
// Hinweis: In AdminPage verwenden wir "allGroups" aus GroupContext,
// damit Superadmins bei der Zugriffsverwaltung alle Gruppen sehen.
import { useAuth } from '../AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
  Box, Button, TextField, Typography, Paper, Alert, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Chip, Divider,
  Checkbox, FormControlLabel, FormGroup, Select, MenuItem, IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Initiale Admin-Emails (wird in Firestore gespeichert)
const DEFAULT_ADMINS = ["simonzilker@yahoo.de"];

const AdminPage = () => {
  const { daysOfWeek, maxFreikarten, stundenlohn, rateOptions, bonusOptions, updateConfig } = useTournamentConfig();
  const { groups, allGroups, activeGroup, saveGroup, deleteGroup } = useGroup();
  const manageableGroups = allGroups && allGroups.length ? allGroups : groups;
  const { user, isSuperadmin } = useAuth();
  const ballkidCtx = useContext(BallkidContext);
  const reloadBallkids = ballkidCtx?.reload;
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessList, setAccessList] = useState([]);
  const [newAccessEmail, setNewAccessEmail] = useState("");
  const [newAccessRole, setNewAccessRole] = useState("viewer");
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null); // { headers: [...], rows: [...] }
  const [status, setStatus] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Turniertage Config
  const [editDays, setEditDays] = useState([]);
  const [newDay, setNewDay] = useState("");

  // Freikarten Config
  const [editMaxFreikarten, setEditMaxFreikarten] = useState(4);

  // Billing Config
  const [editStundenlohn, setEditStundenlohn] = useState(13.90);
  const [editRateOptions, setEditRateOptions] = useState([6.95, 13.90]);
  const [editBonusOptions, setEditBonusOptions] = useState([0, 6.95, 13.90, 20.85]);
  const [newRateOption, setNewRateOption] = useState("");
  const [newBonusOption, setNewBonusOption] = useState("");

  // Gruppen-Verwaltung
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupSingular, setNewGroupSingular] = useState("");
  const [editFeatures, setEditFeatures] = useState([]);
  const [confirmDeleteGroupSlug, setConfirmDeleteGroupSlug] = useState(null);

  // Einzelnes Mitglied hinzufuegen
  const [singleMemberName, setSingleMemberName] = useState("");
  const [singleMemberEmail, setSingleMemberEmail] = useState("");
  const [singleMemberSaving, setSingleMemberSaving] = useState(false);

  useEffect(() => {
    setEditDays([...daysOfWeek]);
    setEditMaxFreikarten(maxFreikarten);
    setEditStundenlohn(stundenlohn || 13.90);
    setEditRateOptions([...(rateOptions || [6.95, 13.90])]);
    setEditBonusOptions([...(bonusOptions || [0, 6.95, 13.90, 20.85])]);
  }, [daysOfWeek, maxFreikarten, stundenlohn, rateOptions, bonusOptions]);

  useEffect(() => {
    if (activeGroup) {
      setEditFeatures([...(activeGroup.features || [])]);
    }
  }, [activeGroup]);

  // Zugriffskontrolle laden (nur fuer Superadmins sichtbar)
  useEffect(() => {
    const loadAccess = async () => {
      try {
        const snap = await getDoc(doc(db, "app_config", "access"));
        if (snap.exists()) {
          const data = snap.data();
          setAccessList(data.users || []);
        } else {
          const defaultUsers = DEFAULT_ADMINS.map((email) => ({ email, role: "admin" }));
          await setDoc(doc(db, "app_config", "access"), { users: defaultUsers });
          setAccessList(defaultUsers);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Zugriffsrechte:", error);
      } finally {
        setAccessLoading(false);
      }
    };
    if (user && isSuperadmin) {
      loadAccess();
    } else if (user) {
      setAccessLoading(false);
    }
  }, [user, isSuperadmin]);

  const handleSaveAccess = async () => {
    try {
      await setDoc(doc(db, "app_config", "access"), { users: accessList });
      setStatus({ type: "success", message: "Zugriffsrechte gespeichert." });
    } catch (error) {
      setStatus({ type: "error", message: "Fehler beim Speichern der Zugriffsrechte." });
    }
  };

  const handleAddAccess = () => {
    const email = newAccessEmail.trim().toLowerCase();
    if (!email || accessList.find((u) => u.email === email)) return;
    setAccessList([
      ...accessList,
      { email, role: newAccessRole, groupSlugs: [] },
    ]);
    setNewAccessEmail("");
  };

  const handleRemoveAccess = (email) => {
    if (DEFAULT_ADMINS.includes(email)) return;
    setAccessList(accessList.filter((u) => u.email !== email));
  };

  const handleChangeRole = (email, role) => {
    if (DEFAULT_ADMINS.includes(email)) return;
    setAccessList(accessList.map((u) => u.email === email ? { ...u, role } : u));
  };

  const handleToggleUserGroup = (email, slug) => {
    if (DEFAULT_ADMINS.includes(email)) return;
    setAccessList(
      accessList.map((u) => {
        if (u.email !== email) return u;
        const current = Array.isArray(u.groupSlugs) ? u.groupSlugs : [];
        const next = current.includes(slug)
          ? current.filter((s) => s !== slug)
          : [...current, slug];
        return { ...u, groupSlugs: next };
      })
    );
  };

  const handleAddDay = () => {
    const trimmed = newDay.trim();
    if (trimmed && !editDays.includes(trimmed)) {
      setEditDays([...editDays, trimmed]);
      setNewDay("");
    }
  };

  const handleRemoveDay = (day) => {
    setEditDays(editDays.filter(d => d !== day));
  };

  const handleSaveConfig = async () => {
    const newConfig = {
      daysOfWeek: editDays,
      maxFreikarten: editMaxFreikarten,
      stundenlohn: editStundenlohn,
      rateOptions: editRateOptions.sort((a, b) => a - b),
      bonusOptions: editBonusOptions.sort((a, b) => a - b),
    };
    await saveTournamentConfig(newConfig);
    updateConfig(newConfig);
    setStatus({ type: "success", message: "Konfiguration gespeichert." });
  };

  const handleSaveFeatures = async () => {
    if (!activeGroup) return;
    await saveGroup({ ...activeGroup, features: editFeatures });
    setStatus({ type: "success", message: `Features fuer "${activeGroup.name}" gespeichert.` });
  };

  const handleToggleFeature = (featureKey) => {
    setEditFeatures((prev) =>
      prev.includes(featureKey) ? prev.filter((f) => f !== featureKey) : [...prev, featureKey]
    );
  };

  const handleCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    if (!slug || groups.find((g) => g.slug === slug)) {
      setStatus({ type: "error", message: "Gruppenname ist ungueltig oder existiert bereits." });
      return;
    }

    await saveGroup({
      name,
      slug,
      memberLabel: name,
      memberSingular: newGroupSingular.trim() || name.replace(/e?n$/, ""),
      features: ALL_FEATURES.map((f) => f.key),
      config: {
        daysOfWeek: [...daysOfWeek],
        maxFreikarten: 4,
        stundenlohn: 13.90,
        rateOptions: [6.95, 13.90],
        bonusOptions: [0, 6.95, 13.90, 20.85],
      },
    });

    setNewGroupName("");
    setNewGroupSingular("");
    setStatus({ type: "success", message: `Gruppe "${name}" erstellt.` });
  };

  const handleExportGroup = async (slug) => {
    try {
      const groupName = groups.find((g) => g.slug === slug)?.name || slug;
      setStatus({ type: "info", message: `Exportiere Daten von "${groupName}"...` });
      const data = await exportGroupData(slug);

      const wb = new ExcelJS.Workbook();
      Object.entries(data).forEach(([colName, records]) => {
        const ws = wb.addWorksheet(colName.slice(0, 31));
        if (records.length > 0) {
          const keys = [...new Set(records.flatMap((r) => Object.keys(r)))];
          ws.columns = keys.map((key) => ({ header: key, key }));
          records.forEach((record) => {
            const row = {};
            keys.forEach((k) => {
              const val = record[k];
              row[k] = typeof val === "object" ? JSON.stringify(val) : val;
            });
            ws.addRow(row);
          });
        }
      });

      const buffer = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${groupName}_Export.xlsx`);
      setStatus({ type: "success", message: `Daten von "${groupName}" exportiert.` });
    } catch (error) {
      console.error("Export-Fehler:", error);
      setStatus({ type: "error", message: "Fehler beim Export: " + error.message });
    }
  };

  const handleDeleteGroup = async (slug) => {
    try {
      const groupName = groups.find((g) => g.slug === slug)?.name || slug;
      setStatus({ type: "info", message: `Loesche alle Daten von "${groupName}"...` });
      await clearAllCollections(slug);
      await deleteGroup(slug);
      setConfirmDeleteGroupSlug(null);
      setStatus({ type: "success", message: `Gruppe "${groupName}" und alle zugehoerigen Daten geloescht.` });
    } catch (error) {
      console.error("Fehler beim Loeschen der Gruppe:", error);
      setStatus({ type: "error", message: "Fehler beim Loeschen: " + error.message });
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setPreviewData(null);

    if (!selectedFile) return;

    try {
      const buffer = await selectedFile.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      const worksheet = workbook.worksheets[0];

      // row.values ist zuverlaessiger als eachCell
      const rows = [];
      worksheet.eachRow((row) => {
        // row.values ist 1-indexed (Index 0 ist undefined), daher slice(1)
        const vals = (row.values || []).slice(1);
        const cells = vals.map((v) => (v != null ? String(v).trim() : ""));
        rows.push(cells);
      });

      if (rows.length === 0) {
        setStatus({ type: "error", message: "Die Datei ist leer." });
        return;
      }

      // Erste Zeile = Header
      const headers = rows[0].map((h) => h.toLowerCase());
      const dataRows = rows.slice(1).filter((r) => r.some((c) => c));

      // Namen zusammenbauen — praezise Header-Erkennung
      const vornameIdx = headers.findIndex((h) =>
        h === "vorname" || h === "first name" || h === "firstname" || h === "first"
      );
      const nachnameIdx = headers.findIndex((h) =>
        h === "nachname" || h === "last name" || h === "lastname" || h === "last" || h === "familienname" || h === "zuname"
      );
      const nameIdx = headers.findIndex((h) =>
        h === "name" || h === "vollname" || h === "fullname" || h === "full name"
      );

      let names;
      if (vornameIdx >= 0 && nachnameIdx >= 0 && vornameIdx !== nachnameIdx) {
        // Vorname + Nachname Spalten gefunden
        names = dataRows.map((r) => `${r[vornameIdx] || ""} ${r[nachnameIdx] || ""}`.trim()).filter(Boolean);
      } else if (nachnameIdx >= 0 && vornameIdx < 0) {
        // Nur Nachname-Spalte, pruefen ob direkt daneben ein Vorname steht
        const possibleVornameIdx = nachnameIdx === 0 ? 1 : nachnameIdx - 1;
        if (possibleVornameIdx < headers.length && possibleVornameIdx >= 0) {
          names = dataRows.map((r) => {
            const nn = (r[nachnameIdx] || "").trim();
            const vn = (r[possibleVornameIdx] || "").trim();
            return vn ? `${vn} ${nn}` : nn;
          }).filter(Boolean);
        } else {
          names = dataRows.map((r) => (r[nachnameIdx] || "").trim()).filter(Boolean);
        }
      } else if (nameIdx >= 0) {
        // Einzelne Name-Spalte
        names = dataRows.map((r) => (r[nameIdx] || "").trim()).filter(Boolean);
      } else if (headers.length === 1 || (headers.length > 0 && !headers[0])) {
        // Nur eine Spalte oder kein Header — alles als Namen nehmen
        names = rows.map((r) => (r[0] || "").trim()).filter(Boolean);
      } else if (headers.length >= 2) {
        // Fallback: Erste zwei Spalten als Nachname + Vorname (oder umgekehrt)
        names = dataRows.map((r) => `${r[0] || ""} ${r[1] || ""}`.trim()).filter(Boolean);
      } else {
        names = dataRows.map((r) => (r[0] || "").trim()).filter(Boolean);
      }

      setPreviewData({
        headers: rows[0],
        sampleRows: dataRows.slice(0, 5),
        names,
        vornameIdx,
        nachnameIdx,
        nameIdx,
      });
    } catch (err) {
      setStatus({ type: "error", message: "Fehler beim Lesen der Datei: " + err.message });
    }
  };

  const handleUpload = async () => {
    if (!previewData || previewData.names.length === 0) return;
    try {
      const label = activeGroup?.memberLabel || "Mitglieder";
      setStatus({ type: "info", message: `Lade ${previewData.names.length} ${label} hoch...` });
      await uploadBallkids(previewData.names, editDays);
      invalidateMembersCache();
      if (reloadBallkids) await reloadBallkids();
      setStatus({ type: "success", message: `${previewData.names.length} ${label} erfolgreich hochgeladen.` });
      setFile(null);
      setPreviewData(null);
    } catch (err) {
      setStatus({ type: "error", message: "Fehler beim Hochladen: " + err.message });
    }
  };

  const handleAddSingleMember = async () => {
    const name = singleMemberName.trim();
    if (!name) return;
    const email = singleMemberEmail.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus({ type: "error", message: "Bitte eine gueltige E-Mail-Adresse eingeben." });
      return;
    }
    const label = activeGroup?.memberSingular || activeGroup?.memberLabel || "Mitglied";
    setSingleMemberSaving(true);
    try {
      await addSingleBallkid(name, editDays, email);
      invalidateMembersCache();
      if (reloadBallkids) await reloadBallkids();
      setSingleMemberName("");
      setSingleMemberEmail("");
      setStatus({ type: "success", message: `${label} "${name}" hinzugefuegt.` });
    } catch (err) {
      setStatus({ type: "error", message: "Fehler beim Hinzufuegen: " + err.message });
    } finally {
      setSingleMemberSaving(false);
    }
  };

  const handleClearAll = async () => {
    setConfirmDeleteOpen(false);
    setDeleteConfirmText("");
    try {
      setStatus({ type: "info", message: "Daten werden geloescht..." });
      await clearAllCollections();
      setStatus({ type: "success", message: "Alle Daten der aktiven Gruppe geloescht. Seite wird neu geladen..." });
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error("Fehler beim Loeschen:", error);
      setStatus({ type: "error", message: "Fehler beim Loeschen: " + error.message });
    }
  };

  if (accessLoading) {
    return <Box sx={{ textAlign: "center", py: 4 }}><Typography>Lade...</Typography></Box>;
  }

  if (!isSuperadmin) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Paper sx={{ p: 4, maxWidth: 400, width: "100%", textAlign: "center" }}>
          <Typography variant="h5" sx={{ mb: 2 }}>Kein Zugriff</Typography>
          <Typography variant="body2" color="text.secondary">
            Die Admin-Tools sind nur fuer Superadmins zugaenglich. Dein Account ({user?.email}) hat keine Superadmin-Berechtigung.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const memberLabel = activeGroup?.memberLabel || "Mitglieder";

  return (
    <Box>
      <Typography variant="h5" sx={{ textAlign: "center", mb: 3 }}>Admin-Tools</Typography>

      {status && (
        <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus(null)}>
          {status.message}
        </Alert>
      )}

      {/* Check-In Test */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Check-In Tester</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Teste den QR-Code Check-In Flow ohne zwei Geraete. Simuliert Ballkind-Seite + Scanner.
        </Typography>
        <Button component={Link} to="checkin-test" variant="outlined" color="primary">
          Check-In Test oeffnen
        </Button>
      </Paper>

      {/* Gruppen-Verwaltung */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Gruppen verwalten</Typography>

        {/* Bestehende Gruppen */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {groups.map((g) => (
            <Chip
              key={g.slug}
              label={g.name}
              color={g.slug === activeGroup?.slug ? "primary" : "default"}
              onDelete={groups.length > 1 ? () => setConfirmDeleteGroupSlug(g.slug) : undefined}
              deleteIcon={groups.length > 1 ? <DeleteIcon /> : undefined}
            />
          ))}
        </Box>

        {/* Neue Gruppe erstellen */}
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <TextField
            label="Neuer Gruppenname (z.B. Linienrichter)"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
          />
          <TextField
            label="Singular (z.B. Linienrichter)"
            value={newGroupSingular}
            onChange={(e) => setNewGroupSingular(e.target.value)}
            size="small"
            sx={{ width: 200 }}
          />
          <Button variant="outlined" onClick={handleCreateGroup} disabled={!newGroupName.trim()}>
            Erstellen
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Features fuer aktive Gruppe */}
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Features fuer "{activeGroup?.name}"
        </Typography>
        <FormGroup row sx={{ mb: 2 }}>
          {ALL_FEATURES.map((f) => (
            <FormControlLabel
              key={f.key}
              control={
                <Checkbox
                  checked={editFeatures.includes(f.key)}
                  onChange={() => handleToggleFeature(f.key)}
                />
              }
              label={f.label}
            />
          ))}
        </FormGroup>
        <Button variant="contained" size="small" onClick={handleSaveFeatures}>
          Features speichern
        </Button>
      </Paper>

      {/* Turniertage Konfiguration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Konfiguration ({activeGroup?.name})</Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Turniertage</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {editDays.map((day) => (
            <Chip
              key={day}
              label={day}
              onDelete={() => handleRemoveDay(day)}
              deleteIcon={<DeleteIcon />}
            />
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            label="Neuer Tag (z.B. 20. April)"
            value={newDay}
            onChange={(e) => setNewDay(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddDay()}
            size="small"
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" onClick={handleAddDay}>Hinzufuegen</Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Max. Freikarten pro Mitglied</Typography>
        <TextField
          type="number"
          value={editMaxFreikarten}
          onChange={(e) => setEditMaxFreikarten(parseInt(e.target.value) || 0)}
          size="small"
          sx={{ width: 120, mb: 2 }}
          inputProps={{ min: 0 }}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Abrechnung</Typography>

        <TextField
          label="Stundenlohn (EUR)"
          type="number"
          value={editStundenlohn}
          onChange={(e) => setEditStundenlohn(parseFloat(e.target.value) || 0)}
          size="small"
          sx={{ width: 160, mb: 2 }}
          inputProps={{ min: 0, step: 0.01 }}
        />

        <Typography variant="caption" display="block" sx={{ mb: 1 }}>Tagessatz-Optionen (EUR)</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {editRateOptions.map((rate) => (
            <Chip
              key={rate}
              label={rate.toFixed(2).replace(".", ",")}
              onDelete={() => setEditRateOptions(editRateOptions.filter(r => r !== rate))}
              deleteIcon={<DeleteIcon />}
            />
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            label="Neuer Tagessatz"
            type="number"
            value={newRateOption}
            onChange={(e) => setNewRateOption(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const val = parseFloat(newRateOption);
                if (val > 0 && !editRateOptions.includes(val)) {
                  setEditRateOptions([...editRateOptions, val]);
                  setNewRateOption("");
                }
              }
            }}
            size="small"
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ width: 140 }}
          />
          <Button variant="outlined" size="small" onClick={() => {
            const val = parseFloat(newRateOption);
            if (val > 0 && !editRateOptions.includes(val)) {
              setEditRateOptions([...editRateOptions, val]);
              setNewRateOption("");
            }
          }}>+</Button>
        </Box>

        <Typography variant="caption" display="block" sx={{ mb: 1 }}>Bonus-Optionen (EUR)</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {editBonusOptions.map((bonus) => (
            <Chip
              key={bonus}
              label={bonus.toFixed(2).replace(".", ",")}
              onDelete={bonus === 0 ? undefined : () => setEditBonusOptions(editBonusOptions.filter(b => b !== bonus))}
              deleteIcon={bonus === 0 ? undefined : <DeleteIcon />}
            />
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            label="Neuer Bonus"
            type="number"
            value={newBonusOption}
            onChange={(e) => setNewBonusOption(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const val = parseFloat(newBonusOption);
                if (val > 0 && !editBonusOptions.includes(val)) {
                  setEditBonusOptions([...editBonusOptions, val]);
                  setNewBonusOption("");
                }
              }
            }}
            size="small"
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ width: 140 }}
          />
          <Button variant="outlined" size="small" onClick={() => {
            const val = parseFloat(newBonusOption);
            if (val > 0 && !editBonusOptions.includes(val)) {
              setEditBonusOptions([...editBonusOptions, val]);
              setNewBonusOption("");
            }
          }}>+</Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSaveConfig}>
            Konfiguration speichern
          </Button>
        </Box>
      </Paper>

      {/* Mitglieder Upload */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{memberLabel} importieren</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>So funktioniert der Import:</Typography>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>Erstelle eine <strong>Excel-Datei (.xlsx)</strong></li>
            <li>Die <strong>erste Zeile</strong> muss die Spaltenueberschriften enthalten, z.B.:
              <br /><code>Vorname | Nachname</code> oder <code>Name</code></li>
            <li>Ab Zeile 2 stehen die Daten (ein Eintrag pro Zeile)</li>
            <li>Lade die Datei hier hoch — du siehst eine Vorschau bevor importiert wird</li>
          </ol>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Erkannte Spalten: <strong>Vorname</strong> + <strong>Nachname</strong> (werden zusammengefuegt) oder <strong>Name</strong> (einzelne Spalte).
            <br />Alle importierten {memberLabel} werden automatisch fuer alle konfigurierten Turniertage als <strong>verfuegbar</strong> markiert.
          </Typography>
        </Alert>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
        </Box>

        {/* Vorschau */}
        {previewData && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Erkannte Spalten: <strong>{previewData.headers.join(" | ")}</strong>
            </Typography>
            {previewData.vornameIdx >= 0 && previewData.nachnameIdx >= 0 && (
              <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                Vorname (Spalte {previewData.vornameIdx + 1}) + Nachname (Spalte {previewData.nachnameIdx + 1}) werden zusammengefuegt.
              </Typography>
            )}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Vorschau ({previewData.names.length} {memberLabel} erkannt):
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, mb: 2, maxHeight: 200, overflow: "auto" }}>
              {previewData.names.slice(0, 10).map((name, i) => (
                <Typography key={i} variant="body2">{i + 1}. {name}</Typography>
              ))}
              {previewData.names.length > 10 && (
                <Typography variant="body2" color="text.secondary">... und {previewData.names.length - 10} weitere</Typography>
              )}
            </Paper>
            <Button variant="contained" onClick={handleUpload} disabled={previewData.names.length === 0}>
              {previewData.names.length} {memberLabel} importieren
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Einzelnes {activeGroup?.memberSingular || "Mitglied"} hinzufuegen
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fuegt eine einzelne Person zur Gruppe "{activeGroup?.name}" hinzu. Sie wird automatisch fuer alle konfigurierten Turniertage als verfuegbar markiert.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField
            label="Vor- und Nachname"
            value={singleMemberName}
            onChange={(e) => setSingleMemberName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && singleMemberName.trim() && !singleMemberSaving) {
                handleAddSingleMember();
              }
            }}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            disabled={singleMemberSaving}
          />
          <TextField
            label="E-Mail (optional)"
            type="email"
            value={singleMemberEmail}
            onChange={(e) => setSingleMemberEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && singleMemberName.trim() && !singleMemberSaving) {
                handleAddSingleMember();
              }
            }}
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            disabled={singleMemberSaving}
          />
          <Button
            variant="contained"
            onClick={handleAddSingleMember}
            disabled={!singleMemberName.trim() || singleMemberSaving}
          >
            {singleMemberSaving ? "Speichere..." : "Hinzufuegen"}
          </Button>
        </Box>
      </Paper>

      {/* Zugriffsverwaltung */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Zugriffsverwaltung</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Lege fest, welche Nutzer (per E-Mail) auf die App zugreifen koennen, welche Rolle sie haben
          und auf welche Gruppen sie Zugriff erhalten. Ohne Gruppen-Auswahl ist der Nutzer fuer alle
          Gruppen freigeschaltet.
        </Typography>

        {/* Bestehende Nutzer */}
        <Box sx={{ mb: 2 }}>
          {accessList.map((u) => {
            const isDefault = DEFAULT_ADMINS.includes(u.email);
            const userGroups = Array.isArray(u.groupSlugs) ? u.groupSlugs : [];
            return (
              <Box
                key={u.email}
                sx={{
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 2,
                  p: 1.5,
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 180, wordBreak: "break-all" }}>
                    {u.email}
                  </Typography>
                  <Select
                    value={u.role}
                    onChange={(e) => handleChangeRole(u.email, e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                    disabled={isDefault}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="viewer">Betrachter</MenuItem>
                  </Select>
                  {!isDefault && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveAccess(u.email)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ mt: 1.25 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Gruppen-Zugriff
                    {isDefault && " (Admin sieht immer alle Gruppen)"}
                    {!isDefault && userGroups.length === 0 && " — aktuell: alle Gruppen"}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {manageableGroups.map((g) => {
                      const selected = userGroups.includes(g.slug);
                      return (
                        <Chip
                          key={g.slug}
                          label={g.name}
                          size="small"
                          color={selected ? "primary" : "default"}
                          variant={selected ? "filled" : "outlined"}
                          onClick={
                            isDefault ? undefined : () => handleToggleUserGroup(u.email, g.slug)
                          }
                          disabled={isDefault}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Neuen Nutzer hinzufuegen */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            label="E-Mail"
            value={newAccessEmail}
            onChange={(e) => setNewAccessEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddAccess()}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Select value={newAccessRole} onChange={(e) => setNewAccessRole(e.target.value)} size="small" sx={{ minWidth: 120 }}>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="viewer">Betrachter</MenuItem>
          </Select>
          <Button variant="outlined" onClick={handleAddAccess}>Hinzufuegen</Button>
        </Box>

        <Button variant="contained" onClick={handleSaveAccess} sx={{ mt: 2 }}>
          Zugriffsrechte speichern
        </Button>
      </Paper>

      {/* Danger Zone */}
      <Paper sx={{ p: 3, border: "2px solid", borderColor: "error.main" }}>
        <Typography variant="h6" sx={{ mb: 2, color: "error.main" }}>Gefahrenzone</Typography>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Loescht ALLE Daten der aktiven Gruppe "{activeGroup?.name}" (Mitglieder, Einteilungen, Freikarten, Anwesenheit, etc.).
          Dieser Vorgang kann nicht rueckgaengig gemacht werden!
        </Typography>
        <Button variant="contained" color="error" onClick={() => setConfirmDeleteOpen(true)}>
          Alle Daten loeschen ({activeGroup?.name})
        </Button>
      </Paper>

      {/* Confirm Delete Data Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => { setConfirmDeleteOpen(false); setDeleteConfirmText(""); }}>
        <DialogTitle>Wirklich ALLE Daten von "{activeGroup?.name}" loeschen?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Tippe <strong>LOESCHEN</strong> ein, um zu bestaetigen:
          </DialogContentText>
          <TextField
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="LOESCHEN"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmDeleteOpen(false); setDeleteConfirmText(""); }}>Abbrechen</Button>
          <Button onClick={handleClearAll} color="error" variant="contained" disabled={deleteConfirmText !== "LOESCHEN"}>
            Unwiderruflich loeschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Group Dialog */}
      <Dialog open={!!confirmDeleteGroupSlug} onClose={() => setConfirmDeleteGroupSlug(null)}>
        <DialogTitle>Gruppe unwiderruflich loeschen?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Die Gruppe "<strong>{groups.find((g) => g.slug === confirmDeleteGroupSlug)?.name}</strong>" und
            <strong> alle zugehoerigen Daten</strong> (Mitglieder, Einteilungen, Check-Ins, Freikarten, Abrechnung etc.)
            werden unwiderruflich geloescht.
          </DialogContentText>
          <DialogContentText>
            Empfehlung: Vorher einen Datenexport herunterladen.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteGroupSlug(null)}>Abbrechen</Button>
          <Button onClick={() => handleExportGroup(confirmDeleteGroupSlug)} color="primary" variant="outlined">
            Daten exportieren
          </Button>
          <Button onClick={() => handleDeleteGroup(confirmDeleteGroupSlug)} color="error" variant="contained">
            Gruppe + Daten loeschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPage;
